/**
 * Link text helpers for SEO / accessibility.
 *
 * Lighthouse's "link-text" audit
 * (https://developer.chrome.com/docs/lighthouse/seo/link-text) scores an anchor
 * 0/100 when its accessible text is a generic phrase like "Learn more",
 * "Read more", or "Click here", because such text does not describe where the
 * link goes. These helpers detect those phrases and derive descriptive
 * replacements that incorporate the destination's title.
 */

const GENERIC_LINK_TEXT_PATTERNS: readonly RegExp[] = [
    /^read\s*more$/i,
    /^learn\s*more$/i,
    /^click\s*here$/i,
    /^here$/i,
    /^more$/i,
    /^details$/i,
    /^see\s*more$/i,
    /^view\s*more$/i,
];

/**
 * Check if link text is generic and should be replaced for SEO/a11y.
 */
export function isGenericLinkText(text: string | null | undefined): boolean {
    if (!text) return false;
    return GENERIC_LINK_TEXT_PATTERNS.some((pattern) => pattern.test(text.trim()));
}

/**
 * Resolve descriptive CTA link text.
 *
 * Preserves an author-provided, non-generic label; otherwise derives
 * `Read more: {title}` so the anchor describes its destination. Falls back to
 * the generic "Learn more" only when no title is available to describe the
 * destination.
 */
export function getDescriptiveLinkText(
    label: string | null | undefined,
    title: string | null | undefined,
): string {
    if (label && !isGenericLinkText(label)) {
        return label;
    }
    const trimmedTitle = title?.trim();
    return trimmedTitle ? `Read more: ${trimmedTitle}` : 'Learn more';
}
